import classNames from "classnames";
import { useTranslations } from "next-intl";

import { useGiftCardMedia } from "~/contexts/giftcard-media";
import CrossCountry from "~/types/cross-country";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";

import Icon from "../common/icon";
import NextImage from "../common/next-image";
import styles from "./product-gallery-navigator.module.scss";

type Props = {
  selectedShade: CrossCountry.ChildrenProduct;
  galleryIdx: number;
  onSelect: (galleryIdx: number) => void;
  enabledGallery: boolean;
  className?: string;
  isOpenGallery?: boolean;
};

function getGalleryObjectsData(product: Props["selectedShade"]) {
  const order = Constants.PRODUCT_MEDIA_GALLERY_ORDER;
  const gallery = product?.product_media?.gallery ?? [];
  let media;
  if (product?.extra_media && process.env.NEXT_PUBLIC_EXTRA_MEDIA_PRODUCT_GALLERY_ENABLED) {
    media = product?.extra_media;
    return media;
  }
  media = product?.product_media?.media ?? [];

  return media
    .filter((m) => {
      if (!gallery.includes(m.blob)) return false;
      if (!order.includes(m.name)) return false;
      return true;
    })
    .sort((a, b) => {
      const aSuffixIdx = order.indexOf(a.name);
      const bSuffixIdx = order.indexOf(b.name);

      return aSuffixIdx - bSuffixIdx;
    });
}

export default function ProductGalleryNavigator(props: Props) {
  const t = useTranslations();
  const media = getGalleryObjectsData(props.selectedShade);
  const { mediaUrl: giftcardMediaUrl } = useGiftCardMedia();
  const dynamicContent = media.length > 4 ? styles.contentStart : styles.contentCenter;

  return (
    <ul className={classNames(props.className || styles.navigatorContainer, dynamicContent)} role="radiogroup">
      {(giftcardMediaUrl ? [{ id: props.selectedShade.product_id, url: giftcardMediaUrl }] : media).map((m, idx) => {
        const isExtraMedia = typeof m !== "object";
        const isVideo = isExtraMedia && m.includes("mp4");
        return (
          <li key={isExtraMedia ? m : m.id}>
            <button
              className={classNames(styles.navigatorButton, idx === props.galleryIdx ? styles.active : "")}
              onClick={() => props.onSelect(idx)}
              disabled={!props.enabledGallery}
              aria-label={
                (isVideo ? t("a11y.galleryVideoNavigation") : t("a11y.galleryImageNavigation")) +
                " " +
                props.selectedShade.display_value
              }
              role="radio"
              aria-checked={idx === props.galleryIdx}
            >
              <div className={styles.wrapperImage}>
                {isVideo ? (
                  <Icon className={styles.playIcon} name="play-pink" width={14} height={18} />
                ) : (
                  <NextImage
                    alt={props.selectedShade.display_value}
                    src={isExtraMedia ? m : m.url}
                    width={42}
                    height={42}
                    className={styles.galleryImage}
                    // Using imageUtils.evaImageLoader causes the image to be placed in the center but
                    // not stretched, resulting in an image that looks smaller.
                    // See ECO-342
                    loader={giftcardMediaUrl ? imageUtils.contentfulImageLoader : undefined}
                  />
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
